import { type FC, useId } from "react";

import { MarketsDropdown } from "~/feature/chart/components/MarketsDropdown";
import { ToggleRow } from "~/feature/chart/components/ToggleRow";
import { isConsensusSelection } from "~/feature/chart/utils";
import { EMBED_MAX_SIZE } from "~/feature/embed/constants";
import type { IEmbedEvent } from "~/feature/embed/types";
import { fitsEmbedSize } from "~/feature/embed/utils/fitsEmbedSize";
import { getAutoEmbedSize } from "~/feature/embed/utils/getAutoEmbedSize";
import { getEmbedFitMessage } from "~/feature/embed/utils/getEmbedFitMessage";
import { getEmbedMinSize } from "~/feature/embed/utils/getEmbedMinSize";
import type { IMarket } from "~/types/event";

import { EmbedCode } from "./components/EmbedCode";
import { EmbedPreview } from "./components/EmbedPreview";
import { EmbedSizeField } from "./components/EmbedSizeField";
import { labelByEmbedOption } from "./constants";
import * as Styled from "./EmbedSettings.styles";
import { EmbedProvider, useEmbed } from "./providers/EmbedProvider";
import { EmbedDimensionEnum, EmbedOptionEnum } from "./types";

interface IEmbedSettingsProps {
  event: IEmbedEvent;
  markets: IMarket[];
  defaultSelection: string[];
}

const EmbedSettingsContent: FC = () => {
  const {
    markets,
    selections,
    options,
    size: customSize,
    toggleMarket,
    selectConsensus,
    setOption,
    setSize,
  } = useEmbed();

  const isConsensusMode = isConsensusSelection(markets, selections);
  const visibleOptions = Object.values(EmbedOptionEnum).filter(
    (option) => option !== EmbedOptionEnum.CONSENSUS_ONLY || isConsensusMode
  );
  const minSize = getEmbedMinSize({
    chart: options[EmbedOptionEnum.CHART],
    odds: options[EmbedOptionEnum.ODDS],
    consensusOnly: options[EmbedOptionEnum.CONSENSUS_ONLY],
    selectionCount: selections.length,
    isConsensusMode,
  });
  const size = customSize ?? getAutoEmbedSize(minSize);
  const fitMessage = fitsEmbedSize(size, minSize)
    ? null
    : getEmbedFitMessage(size, minSize);
  const fitMessageId = useId();

  return (
    <div>
      <Styled.Stack>
        <Styled.Controls>
          <Styled.Field>
            <Styled.FieldLabel component="p" variant="caption">
              Market(s)
            </Styled.FieldLabel>
            <MarketsDropdown
              markets={markets}
              selections={selections}
              isConsensusMode={isConsensusMode}
              onToggle={toggleMarket}
              onSelectConsensus={selectConsensus}
            />
          </Styled.Field>

          {visibleOptions.map((option) => (
            <ToggleRow
              key={option}
              label={labelByEmbedOption[option]}
              checked={options[option]}
              onChange={(value) => setOption(option, value)}
            />
          ))}

          <Styled.SizeRow>
            <EmbedSizeField
              dimension={EmbedDimensionEnum.HEIGHT}
              value={size.height}
              min={minSize.height}
              max={EMBED_MAX_SIZE.height}
              errorMessageId={fitMessageId}
              onChange={(height) => setSize({ ...size, height })}
            />
            <Styled.SizeDash aria-hidden>–</Styled.SizeDash>
            <EmbedSizeField
              dimension={EmbedDimensionEnum.WIDTH}
              value={size.width}
              min={minSize.width}
              max={EMBED_MAX_SIZE.width}
              errorMessageId={fitMessageId}
              onChange={(width) => setSize({ ...size, width })}
            />
          </Styled.SizeRow>
        </Styled.Controls>

        <EmbedPreview
          size={size}
          minSize={minSize}
          fitMessageId={fitMessageId}
        />
      </Styled.Stack>

      <EmbedCode size={size} fitMessage={fitMessage} />
    </div>
  );
};

export const EmbedSettings: FC<IEmbedSettingsProps> = ({
  event,
  markets,
  defaultSelection,
}) => (
  <EmbedProvider
    event={event}
    markets={markets}
    defaultSelection={defaultSelection}
  >
    <EmbedSettingsContent />
  </EmbedProvider>
);
